import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import './RealTransactionHybridCard.css';

const MODE_TABS = [
  { key: 'sale', label: '매매' },
  { key: 'lease', label: '전세' },
  { key: 'ratio', label: '전세가율' },
];


const toDateKey = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  if (/^\d{8}$/.test(text)) return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
  if (/^\d{4}-\d{2}$/.test(text)) return `${text}-01`;
  if (text.length >= 10) return text.slice(0, 10);
  return text;
};

const toMonthKey = (value) => {
  const dateKey = toDateKey(value);
  return dateKey ? dateKey.slice(0, 7) : '';
};

// 날짜 표기 통일: YY.MM.DD (일 없으면 YY.MM)
const formatDotDate = (value) => {
  const d = toDateKey(value);
  const m = String(d || '').match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!m) return d ? d.replace(/-/g, '.') : '-';
  const yy = m[1].slice(2);
  return m[3] ? `${yy}.${m[2]}.${m[3]}` : `${yy}.${m[2]}`;
};

const toNullableNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const dealTypeLabel = (dealType, dealTypeName) => {
  if (dealTypeName) return String(dealTypeName);
  if (Number(dealType) === 1) return '매매';
  if (Number(dealType) === 2) return '전세';
  if (Number(dealType) === 3) return '월세';
  return '-';
};

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(Math.round(num));
  // 억 단위: 소수 1자리 ('12.5억' / '13억')
  if (abs >= 100000000) {
    const eok = abs / 100000000;
    const text = Number.isInteger(eok) ? String(eok) : eok.toFixed(1).replace(/\.0$/, '');
    return `${sign}${text}억`;
  }
  if (abs >= 10000) return `${sign}${Math.round(abs / 10000).toLocaleString('ko-KR')}만`;
  return `${sign}${abs.toLocaleString('ko-KR')}원`;
};

const formatPriceToManUnit = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return `${Math.round(num / 10000).toLocaleString('ko-KR')}만원`;
};

const formatAxisToKoreanPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  const abs = Math.abs(Math.round(num));
  const eok = Math.floor(abs / 100000000);
  const sign = num < 0 ? '-' : '';
  return `${sign}${eok.toLocaleString('ko-KR')}억`;
};

const formatAreaSquareMeters = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return num.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
};

const buildAreaTypeLabel = (row = {}) => {
  const planTypeName2 = String(row?.planTypeName2 ?? row?.plan_type_name2 ?? '').trim();
  const exclsSpc = row?.exclsSpc ?? row?.excls_spc;
  const exclsSpcText = formatAreaSquareMeters(exclsSpc);
  if (planTypeName2 && exclsSpcText) return `${planTypeName2}(${exclsSpcText}m2)`;
  if (planTypeName2) return planTypeName2;
  if (exclsSpcText) return `${exclsSpcText}m2`;
  const ptpNo = String(row?.ptpNo ?? row?.ptp_no ?? '').trim();
  return ptpNo || '-';
};

function RealTransactionHybridCard({
  transactions = [],
  chartItems = [],
  monthlyStats = [],
  ptpOptions = [],
  selectedPyeongOptions = [],
  ptpNo = '',
  onPtpChange,
  viewMode = 'sale',
  onViewModeChange,
  loading = false,
  error = '',
  hasMoreRows = false,
  rowsLoadingMore = false,
  onLoadMoreRows,
  visibleRowCount = 20,
}) {

  const ptpMetaMap = useMemo(() => {
    const base = Array.isArray(ptpOptions) ? ptpOptions : [];
    const next = new Map();
    const seen = new Set();
    base.forEach((row) => {
      const value = String(row?.ptpNo ?? row?.ptp_no ?? row?.value ?? '').trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      next.set(value, buildAreaTypeLabel(row));
    });
    return next;
  }, [ptpOptions]);

  const tableRows = useMemo(
    () =>
      (Array.isArray(transactions) ? transactions : [])
        .slice()
        .sort((a, b) => String(b?.tradeDate || '').localeCompare(String(a?.tradeDate || '')))
        .map((row) => {
          const isSale = Number(row?.dealType) === 1;
          const isLease = Number(row?.dealType) === 2;
          const isRent = Number(row?.dealType) === 3;
          const value = isSale ? row?.priceKrw : row?.depositKrw;
          // planTypeName2가 있으면 사용, 없으면 기존 로직 사용
          const areaTypeDisplay = row?.planTypeName2 || row?.planTypeName || buildAreaTypeLabel(row);
          const finalAreaType = areaTypeDisplay !== '-' ? areaTypeDisplay : (ptpMetaMap.get(String(row?.ptpNo || '').trim()) || '-');
          return {
            id: row?.id || `${row?.tradeDate}-${row?.floor}-${row?.dealType}-${row?.priceKrw || row?.depositKrw || 0}`,
            date: row?.tradeDate || '-',
            amount: formatPrice(value),
            dealType: dealTypeLabel(row?.dealType, row?.dealTypeName),
            dealTypeClass: isSale ? 'sale' : isLease ? 'lease' : isRent ? 'rent' : 'etc',
            areaType: finalAreaType,
            buildingDong: '-',
            floor: row?.floor === null || row?.floor === undefined ? '-' : `${row.floor}층`,
          };
        }),
    [transactions, ptpMetaMap]
  );
  const visibleRows = useMemo(
    () => tableRows.slice(0, Math.max(0, Number(visibleRowCount) || 20)),
    [tableRows, visibleRowCount]
  );

  const { priceOption, countOption } = useMemo(() => {
    const MAX_ZOOM_OUT_MONTHS = 84;
    const rawTxItems = Array.isArray(chartItems) ? chartItems : [];
    const monthlyRatioMap = new Map();
    (Array.isArray(monthlyStats) ? monthlyStats : []).forEach((row) => {
      const monthKey = String(row?.monthKey || '').trim();
      const ratio = toNullableNumber(row?.leaseToSaleRatio);
      if (monthKey && ratio != null) monthlyRatioMap.set(monthKey, ratio);
    });

    const filtered = rawTxItems.filter((row) => {
      const dealType = Number(row?.dealType);
      if (dealType !== 1 && dealType !== 2) return false;
      if (viewMode === 'sale') return dealType === 1;
      if (viewMode === 'lease') return dealType === 2;
      return true;
    });

    const byMonth = new Map();
    filtered.forEach((row) => {
      const monthKey = toMonthKey(row?.tradeDate);
      if (!monthKey) return;
      const amount = Number(row?.dealType) === 1 ? toNullableNumber(row?.priceKrw) : toNullableNumber(row?.depositKrw);
      if (amount == null || amount <= 0) return;
      if (!byMonth.has(monthKey)) byMonth.set(monthKey, { sum: 0, count: 0 });
      const m = byMonth.get(monthKey);
      m.sum += amount;
      m.count += 1;
    });

    const monthsAll = Array.from(new Set([...byMonth.keys(), ...monthlyRatioMap.keys()])).sort();
    const months = monthsAll.slice(Math.max(0, monthsAll.length - MAX_ZOOM_OUT_MONTHS));
    const monthIndexMap = new Map(months.map((m, idx) => [m, idx]));

    const monthlyAvgLine = months.map((monthKey) => {
      const row = byMonth.get(monthKey);
      if (!row || row.count === 0) return null;
      return row.sum / row.count;
    });
    const monthlyCountBars = months.map((monthKey) => {
      const row = byMonth.get(monthKey);
      return row ? row.count : 0;
    });
    const ratioSeries = months.map((monthKey) => monthlyRatioMap.get(monthKey) ?? null);

    const yValues = monthlyAvgLine.filter((v) => Number.isFinite(v));
    const minY = yValues.length ? Math.min(...yValues) : 0;
    const maxY = yValues.length ? Math.max(...yValues) : 100000000;
    const gap = Math.max(1, maxY - minY);
    const pad = Math.max(gap * 0.15, 300000);
    const priceSeriesName = viewMode === 'lease' ? '실거래 전세가' : '실거래 매매가';
    const maxCount = Math.max(...monthlyCountBars, 1);

    let maxPriceValue = 0;
    let maxPriceMonthIndex = -1;
    monthlyAvgLine.forEach((price, idx) => {
      if (price != null && price > maxPriceValue) { maxPriceValue = price; maxPriceMonthIndex = idx; }
    });

    // 상·하단 차트 x축 공통(정렬 일치)
    const sharedX = {
      type: 'category',
      data: months,
      boundaryGap: true,
      axisLine: { lineStyle: { color: '#e3e8ef' } },
      axisTick: { show: false },
    };

    // 상단: 실거래 라인(+전세가율 라인)
    const priceOption = {
      animationDuration: 300,
      // top: 최고가 별표 라벨(3줄)이 잘리지 않도록 충분히 확보
      grid: { left: 56, right: viewMode === 'ratio' ? 44 : 40, top: 48, bottom: 8 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line' },
        backgroundColor: '#ffffff', borderColor: '#e6ebf2', borderWidth: 1, textStyle: { color: '#334155' },
        formatter: (params = []) => {
          if (!params.length) return '';
          const month = String(params[0]?.axisValue || '');
          const avg = monthlyAvgLine[monthIndexMap.get(month)];
          const cnt = byMonth.get(month)?.count || 0;
          return [
            `<div class="tx-tooltip-title">${month.replace('-', '년 ')}월</div>`,
            `<div class="tx-tooltip-meta"><span>금액</span><strong>${formatPrice(avg)}</strong></div>`,
            `<div class="tx-tooltip-meta"><span>건수</span><strong>${cnt.toLocaleString('ko-KR')}건</strong></div>`,
          ].join('');
        },
      },
      xAxis: { ...sharedX, axisLabel: { show: false } }, // 날짜 라벨은 하단 건수 차트에서 표기
      yAxis: [
        {
          type: 'value',
          min: Math.max(0, Math.floor(minY - pad)),
          max: Math.ceil(maxY + pad),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#667085', formatter: (v) => formatAxisToKoreanPrice(v), margin: 14 },
          splitLine: { lineStyle: { color: '#edf1f6' } },
        },
        ...(viewMode === 'ratio' ? [{
          type: 'value', position: 'right', min: 0, max: 100,
          axisLine: { show: false }, axisTick: { show: false },
          axisLabel: { color: '#94a3b8', formatter: (v) => `${Math.round(v)}%` },
          splitLine: { show: false },
        }] : []),
      ],
      series: [
        {
          name: priceSeriesName,
          type: 'line',
          data: monthlyAvgLine,
          smooth: false,
          connectNulls: true,
          showSymbol: false,
          lineStyle: { color: '#f97316', width: 2.4 },
          itemStyle: { color: '#f97316' },
          z: 6,
          markPoint: maxPriceMonthIndex >= 0 ? {
            data: [{
              name: '최고가',
              coord: [maxPriceMonthIndex, maxPriceValue],
              value: formatPrice(maxPriceValue),
              symbol: 'path://M512 64l128.6 260.6L928 366.4l-208 202.7L769.2 864 512 729.6 254.8 864 304 569.1 96 366.4l287.4-41.8L512 64z',
              symbolSize: 22,
              symbolKeepAspect: true,
              itemStyle: { color: '#dc2626', borderColor: '#991b1b', borderWidth: 2.8, shadowColor: '#991b1b', shadowBlur: 10, opacity: 0.95 },
              label: {
                show: true,
                formatter: (params) => `최고가\n${months[maxPriceMonthIndex].replace('-', '.')}\n${params.value}`,
                fontSize: 11, fontWeight: 800, color: '#991b1b', position: 'top',
              },
            }],
          } : undefined,
        },
        ...(viewMode === 'ratio' ? [{
          name: '전세가율', type: 'line', yAxisIndex: 1, data: ratioSeries,
          smooth: 0.25, connectNulls: true, showSymbol: false,
          lineStyle: { color: '#64748b', width: 1.5, type: 'dashed' }, itemStyle: { color: '#64748b' }, z: 5,
        }] : []),
      ],
      legend: { show: false },
    };

    // 하단: 실거래 건수(작게)
    const countOption = {
      animationDuration: 300,
      grid: { left: 56, right: viewMode === 'ratio' ? 44 : 40, top: 6, bottom: 20 },
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'line' },
        backgroundColor: '#ffffff', borderColor: '#e6ebf2', borderWidth: 1, textStyle: { color: '#334155' },
        formatter: (params = []) => {
          if (!params.length) return '';
          const month = String(params[0]?.axisValue || '');
          const cnt = byMonth.get(month)?.count || 0;
          return `<div class="tx-tooltip-title">${month.replace('-', '년 ')}월</div><div class="tx-tooltip-meta"><span>실거래 건수</span><strong>${cnt.toLocaleString('ko-KR')}건</strong></div>`;
        },
      },
      xAxis: { ...sharedX, axisLabel: { color: '#667085', fontSize: 10, formatter: (v) => String(v).slice(2).replace('-', '.') } },
      yAxis: {
        type: 'value', min: 0, max: Math.ceil(maxCount * 1.2),
        axisLine: { show: false }, axisTick: { show: false },
        axisLabel: { color: '#94a3b8', fontSize: 10, formatter: (v) => `${Math.round(v)}`, margin: 8 },
        splitLine: { show: false },
      },
      series: [{
        name: '실거래 건수', type: 'bar', data: monthlyCountBars,
        barWidth: 8, itemStyle: { color: '#cbd5e1', borderRadius: [2, 2, 0, 0] }, z: 2,
      }],
      legend: { show: false },
    };

    return { priceOption, countOption };
  }, [chartItems, monthlyStats, viewMode]);

  // 전세가율 모드용 월별 통계 테이블 데이터
  const monthlyStatsTableRows = useMemo(() => {
    if (viewMode !== 'ratio') return [];

    const rawTxItems = Array.isArray(chartItems) ? chartItems : [];

    const saleByMonth = new Map();
    const leaseByMonth = new Map();

    rawTxItems.forEach((row) => {
      const dealType = Number(row?.dealType);
      if (dealType !== 1 && dealType !== 2) return;

      const monthKey = toMonthKey(row?.tradeDate);
      if (!monthKey) return;

      if (dealType === 1) {
        const amount = toNullableNumber(row?.priceKrw);
        if (amount == null || amount <= 0) return;
        if (!saleByMonth.has(monthKey)) saleByMonth.set(monthKey, { sum: 0, count: 0 });
        const m = saleByMonth.get(monthKey);
        m.sum += amount;
        m.count += 1;
      } else if (dealType === 2) {
        const amount = toNullableNumber(row?.depositKrw);
        if (amount == null || amount <= 0) return;
        if (!leaseByMonth.has(monthKey)) leaseByMonth.set(monthKey, { sum: 0, count: 0 });
        const m = leaseByMonth.get(monthKey);
        m.sum += amount;
        m.count += 1;
      }
    });

    const allMonths = Array.from(new Set([...saleByMonth.keys(), ...leaseByMonth.keys()])).sort();

    return allMonths.map((monthKey) => {
      const saleData = saleByMonth.get(monthKey);
      const leaseData = leaseByMonth.get(monthKey);
      const saleAvg = saleData ? saleData.sum / saleData.count : null;
      const leaseAvg = leaseData ? leaseData.sum / leaseData.count : null;
      const priceDiff = saleAvg != null && leaseAvg != null ? saleAvg - leaseAvg : null;
      const ratio = saleAvg != null && leaseAvg != null ? (leaseAvg / saleAvg) * 100 : null;

      return {
        monthKey,
        saleAvg,
        leaseAvg,
        priceDiff,
        ratio,
      };
    }).reverse(); // 최신순
  }, [chartItems, monthlyStats, viewMode]);

  const hasChartData = (Array.isArray(chartItems) && chartItems.length > 0) || (Array.isArray(monthlyStats) && monthlyStats.length > 0);
  const hasTableData = viewMode === 'ratio' ? monthlyStatsTableRows.length > 0 : tableRows.length > 0;

  const ptpDropdownOptions = useMemo(() => {
    if (!Array.isArray(selectedPyeongOptions) || selectedPyeongOptions.length === 0) return [];
    return selectedPyeongOptions.map((option, idx) => {
      const value = String(option?.ptpNo ?? option?.value ?? '').trim();
      const label = buildAreaTypeLabel(option);
      return { value, label, key: value || `ptp-${idx}` };
    });
  }, [selectedPyeongOptions]);

  return (
    <section className="overview-card tx-hybrid-card-v2">
      <div className="tx-hybrid-head">
        <h4 className="tx-chart-title">실거래</h4>
        <div className="tx-mode-top" role="tablist" aria-label="chart-mode-toggle">
          {MODE_TABS.map((tab) => (
            <button key={tab.key} type="button" className={viewMode === tab.key ? 'active' : ''} onClick={() => onViewModeChange?.(tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="tx-hybrid-content-row">
        <div className="tx-hybrid-chart-col">
          {loading ? (
            <div className="tx-empty-state">실거래 데이터를 불러오는 중입니다...</div>
          ) : error && !hasChartData ? (
            <div className="tx-empty-state">{error}</div>
          ) : !hasChartData ? (
            <div className="tx-empty-state">데이터가 없습니다</div>
          ) : (
            <>
              {/* 상단: 실거래 라인(8) */}
              <div className="tx-hybrid-chart-price">
                <ReactECharts option={priceOption} style={{ width: '100%', height: '100%' }} notMerge lazyUpdate />
              </div>
              {/* 하단: 실거래 건수(2, 작게) */}
              <div className="tx-hybrid-chart-count">
                <ReactECharts option={countOption} style={{ width: '100%', height: '100%' }} notMerge lazyUpdate />
              </div>
            </>
          )}
        </div>

        <div className="tx-hybrid-table-pane">
          {!loading && !hasTableData ? (
            <div className="tx-table-empty-state">데이터가 없습니다</div>
          ) : viewMode === 'ratio' ? (
            <div className="tx-hybrid-table-v2-wrap">
              <table className="tx-hybrid-table-v2 tx-ratio-table">
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>매매가</th>
                    <th>전세가</th>
                    <th>가격차이</th>
                    <th>전세가율</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyStatsTableRows.map((row) => (
                    <tr key={row.monthKey}>
                      <td>{formatDotDate(row.monthKey)}</td>
                      <td className="price">{row.saleAvg != null ? formatPrice(row.saleAvg) : '-'}</td>
                      <td className="price">{row.leaseAvg != null ? formatPrice(row.leaseAvg) : '-'}</td>
                      <td className="price">{row.priceDiff != null ? formatPrice(row.priceDiff) : '-'}</td>
                      <td className="ratio">{row.ratio != null ? `${row.ratio.toFixed(1)}%` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <div className="tx-hybrid-table-v2-wrap">
                <table className="tx-hybrid-table-v2">
                  <thead>
                    <tr>
                      <th>날짜</th>
                      <th>실거래가</th>
                      <th>타입</th>
                      <th>층</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr key={row.id}>
                        <td className="tx-date-cell">
                          <span>{formatDotDate(row.date)}</span>
                        </td>
                        <td className="price">{row.amount}</td>
                        <td>{row.areaType}</td>
                        <td>{row.floor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {hasMoreRows ? (
                <button
                  type="button"
                  className="tx-table-more-btn"
                  onClick={() => onLoadMoreRows?.()}
                  disabled={rowsLoadingMore || loading}
                >
                  {rowsLoadingMore ? '불러오는 중...' : '더보기'}
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default RealTransactionHybridCard;
